import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, styled } from 'dripsy';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { Row } from '@/design-system/components/layout';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList } from './type';
import { PageHeaderModalNative } from '../../../account-management/screens/page/PageHeaderModalNative';
import moment from 'moment-timezone';
import { CardAccountTypeSelectFilters, CardCurrencySelectFilters, CardIdentityTypeSelectFilters } from '../../options';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface CreateCardAccountProps {
  onClose: () => void;
  data: any;
  returnData?: any;
  refresh?: () => void;
}

export const CreateCardAccountNativeTwoScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, data, returnData, refresh } = props;
  const { t } = useTranslation('card-account');
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btnView,
    btn,
    textHeaderTitle,
    textHeaderSubTitle,
    modal
  } = sxStyles;

  const { theme } = useDripsyTheme();

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  return (
    <View sx={{ height: '100%', backgroundColor: '$white' }}>
      <PageHeaderModalNative>
        <Row sx={{ justifyContent: 'center', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account::credit-card-new-account.glossary.new-account')}
            </TextTitle>
          </View>
        </Row>
      </PageHeaderModalNative>
      <View sx={modal}>
        <View sx={topHeader}>
          <Icon name="checkCircleBlue" height={108} width={108} />
          <H6 bold sx={textHeaderTitle}>
            {t('card-account::credit-card-new-account.new-account-placed.title--new-account-new-account-placed')}
          </H6>
          <TextCaption sx={textHeaderSubTitle}>
            {t('card-account::credit-card-new-account.new-account-placed.text--new-account-created')}
          </TextCaption>
        </View>
        <View sx={body}>
          <View sx={{ gap: '$5' }}>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account-name')}
              </TextCaption>
              <H6 sx={textAtmPin}>{data.name}</H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.mobile-number')}
              </TextCaption>
              <H6 sx={textAtmPin}>{`+${data.phoneCode} ${data.phoneNumber}`}</H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account-type')}
              </TextCaption>
              <H6 sx={textAtmPin}>{CardIdentityTypeSelectFilters(t).find((el) => el.value === data.identityType)?.label}</H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.card-type')}
              </TextCaption>
              <H6 sx={textAtmPin}>{CardAccountTypeSelectFilters(t).find((el) => el.value === data.accountType)?.label}</H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.currency')}
              </TextCaption>
              <H6 sx={textAtmPin}>{CardCurrencySelectFilters(t).find((el) => el.value === data.currency)?.label}</H6>
            </View>
            {data.accountType == 'asset-link' && data.assetLink && (
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account::credit-card-new-account.glossary.account')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {data.assetLink.name} ({data.assetLink.accountNumber})
                </H6>
              </View>
            )}
            {data.accountType == 'fixed-limit' && data.creditAccount && (
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account::credit-card-new-account.glossary.credit-account')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {data.creditAccount.name} ({data.creditAccount.accountNumber})
                </H6>
              </View>
            )}
            {data.accountType == 'fixed-limit' && data.limit && (
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account::credit-card-new-account.glossary.limit')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {data.currency}
                </H6>
              </View>
            )}
            {data.accountType == 'fixed-limit' && data.settlementAccount && (
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account::credit-card-new-account.glossary.settlement-account')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {data.settlementAccount.name} ({data.settlementAccount.accountNumber})
                </H6>
              </View>
            )}
            {data.individual && data?.identityType == 'individual' && (
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account::credit-card-new-account.glossary.account-holder')}
                </TextCaption>
                <H6 sx={textAtmPin}>{`${data.individual?.firstName} ${data.individual?.lastName}`}</H6>
              </View>
            )}
          </View>
          <View sx={btnView}>
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::general.button--close')}
              onPress={() => {
                refresh && refresh();
                navigation.getParent()?.goBack();
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    padding: '$5'
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault'
  },
  body: {
    py: 28
  },
  horizontalWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  textLabel: {
    color: '$textSecondary'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2',
    textAlign: 'center'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  },
  timeView: {
    mb: '$3',
    mt: '$5'
  },
  refView: {
    mb: '$10'
  }
};
